import React, {useEffect, useRef, useState} from 'react';
import styled from 'styled-components';
import {useUserDataContext} from '@bootcamp/web/src/contexts/UserData';
import {Line} from 'react-chartjs-2';
import 'chartjs-adapter-moment';
import {LoadingSpinner, StyledLabel, StyledHeader} from './shared';
import {TableOverflowOuterWrapper, TableOverflowInnerWrapper} from '@bootcamp/web/src/bootcamps/components/Performance/shared';
import blurredBackground from '@bootcamp/shared/src/assets/images/dat-performance.png';
import {getResults} from '@bootcamp/shared/src/util/scoring';

import theme from '@bootcamp/shared/src/styles/theme';
import { AutoHeightParent } from '../../../components/AutoHeightParent';

const SUBJECTS = ['Biology', 'General Chemistry', 'Organic Chemistry', 'Perceptual Ability', 'Reading Comprehension', 'Quantitative Reasoning'];

const subjectColorMap = {
  'Biology': '#53A3DF',
  'General Chemistry': '#45BCBA',
  'Organic Chemistry': '#ED9625',
  'Perceptual Ability': '#BA3996',
  'Reading Comprehension': '#DC517C',
  'Quantitative Reasoning': '#6946BC',
};

const Wrapper = styled.div`
  background: white;
  border-radius: 8px;
  padding: 24px;
`;
const HeaderText = styled(StyledLabel)`
  margin-bottom: 0;
`;
const Container = styled.div`
  margin-bottom: 32px;
  position: relative;
`;

const ChartBackground = styled.div`
  background: white;
  border: 1px solid ${({theme}) => theme.colors.borderPalette.primary};
  padding: 24px 16px 12px;
  border-radius: 8px;
  height: 350px;
  display: flex;
  align-items: center;
  justify-content: center;
`;
const SubjectSelect = styled.select`
  background: white;
  border: 1px solid ${({theme}) => theme.colors.borderPalette.primary};
  border-radius: 8px;
  padding: 12px 16px;
  color: ${({theme}) => theme.colors.typographyPalette.primary};
  width: 258px;
  cursor: pointer;
  font-family: proxima-nova;
  font-size: 18px;
  font-weight: 600;
  line-height: 100%;
  // custom caret with two-line arrow
  appearance: none;
  background-image: url("data:image/svg+xml,%3Csvg%20xmlns%3D%22http%3A%2F%2Fwww.w3.org%2F2000%2Fsvg%22%20width%3D%2216%22%20height%3D%2216%22%20viewBox%3D%220%200%2016%2016%22%20fill%3D%22none%22%3E%0A%20%20%3Cpath%20d%3D%22M2.81322%205.31334C2.90697%205.21971%203.03405%205.16711%203.16655%205.16711C3.29905%205.16711%203.42614%205.21971%203.51989%205.31334L7.99989%209.79334L12.4799%205.31334C12.5747%205.22502%2012.7%205.17694%2012.8296%205.17922C12.9591%205.18151%2013.0827%205.23398%2013.1743%205.32559C13.2659%205.4172%2013.3184%205.54079%2013.3207%205.67033C13.323%205.79986%2013.2749%205.92522%2013.1866%206.02001L8.35322%2010.8533C8.25947%2010.947%208.13239%2010.9996%207.99989%2010.9996C7.86738%2010.9996%207.7403%2010.947%207.64655%2010.8533L2.81322%206.02001C2.71959%205.92626%202.66699%205.79917%202.66699%205.66667C2.66699%205.53417%202.71959%205.40709%202.81322%205.31334Z%22%20fill%3D%22%232F3037%22%2F%3E%0A%3C%2Fsvg%3E");
  background-repeat: no-repeat;
  background-position-x: 95%;
  background-position-y: 50%;
  &:focus {
    outline: none;
  }
`;
const SelectArea = styled.div`
  display: flex;
  flex-direction: row;
  gap: 8px;
  margin-bottom: 12px;

  ${({theme}) => theme.mediaQueries.tablet} {
    flex-direction: column;
  }
`;



export const PerformanceChart = ({averages, fetching, subjectOverride='', dataByTest, parentHasPadding}) => {
  const {searchUserInteractions, bootcamp} = useUserDataContext();
  const [selectedSubject, setSelectedSubject] = useState(subjectOverride);
  const [gradient, setGradient] = useState();
  const chartRef = useRef();
  const wrapperRef = useRef();

  const {sortOrder} = searchUserInteractions(`performance_order`, 'DESC');
  const performanceRecord = searchUserInteractions(`${bootcamp}_performance`);

  // const subjectAverage = subjectAverages?.[selectedSubject]?.avgScore;
  const subjectAverageScore = 390 // getResults(subjectAverage * 100, `${selectedSubject} Test #1`, selectedSubject)?.score || 18;

  const testResultsOverTime = performanceRecord
    .results
    ?.map(({testSubject, testNumber, score, createdAt}) => {
      const translatedScore = score;
      return ({ testSubject, testNumber, score: translatedScore, createdAt });
    }) || [];
  const data = [...testResultsOverTime].sort((a, b) => sortOrder === 'DESC' ? new Date(b.createdAt) - new Date(a.createdAt) : new Date(a.createdAt) - new Date(b.createdAt));
  const filteredTestResultsOverTime = data.filter(({testSubject}) => testSubject === selectedSubject || selectedSubject === 'All');

  const maxChartValue = 600;

  useEffect(() => {
    if (fetching) return;
    if (!selectedSubject) {
      for (const subject of SUBJECTS) {
        const firstSubject = data.find(({testSubject}) => testSubject === subject);
        if (firstSubject) {
          setSelectedSubject(firstSubject.testSubject);
          break;
        }
      }
      setSelectedSubject(SUBJECTS[0])
    }

    const ctx = chartRef.current?.ctx;
    let gradient;
    if (ctx && selectedSubject) {
      // create a similar linear gradient to the following:
      // linear-gradient(180deg, rgba(98, 85, 229, 0.20) 27.22%, rgba(98, 85, 229, 0.05) 100%);
      gradient = ctx.createLinearGradient(0, 0, 0, chartRef.current.chartArea.bottom - chartRef.current.chartArea.top);
      gradient.addColorStop(0, `${subjectColorMap[selectedSubject]}AA`);
      gradient.addColorStop(1, `${subjectColorMap[selectedSubject]}10`);
      setGradient(gradient)
    }

  }, [fetching, selectedSubject, chartRef.current]);

  const dataset = {
    label: selectedSubject,
    data: filteredTestResultsOverTime.reduce((acc, {testSubject: ts, score, createdAt, testNumber}) => {
      if (ts !== selectedSubject) return acc;
      return [
        ...acc,
        {x: createdAt, y: score, dataLabel: `${ts} #${testNumber}: ${score}`}
      ];
    }, []),
    borderColor: subjectColorMap[selectedSubject],
    pointColor: subjectColorMap[selectedSubject],
    pointBackgroundColor: subjectColorMap[selectedSubject],
    pointRadius: 5,
    pointHoverRadius: 6,
    hoverBorderWidth: 5,
    hitRadius: 8,
    borderWidth: 3,
    cubicInterpolationMode: 'monotone',
    fill: true,
    backgroundColor: gradient,
  };

  const averageDataset =  {
    label: 'Average Score',
    data: filteredTestResultsOverTime.map(result => ({
      x: result.createdAt,
      y: subjectAverageScore,
    })),
    borderColor: '#000000', // Black for high contrast
    borderWidth: 2,
    borderDash: [2, 2], // Dotted or dashed line style
    pointRadius: 0, // Hide points on the average line
    pointHoverRadius: 0,
    pointHitRadius: 30, // Expands the hover area
    hoverBorderWidth: 0, // Make the line stand out when hovered
    fill: false,
  };

  const chartData = {
    labels: filteredTestResultsOverTime.map(({createdAt}) => createdAt),
    datasets: [dataset, averageDataset]
  };

  function customTooltipHandler(context) {
    const tooltipModel = context.tooltip;

    // Hide tooltip when no tooltip is active
    if (!tooltipModel || tooltipModel.opacity === 0) {
      const tooltipEl = document.getElementById("custom-tooltip");
      if (tooltipEl) tooltipEl.style.opacity = "0";
      return;
    }

    // Create tooltip container if it doesn't exist
    let tooltipEl = document.getElementById("custom-tooltip");

    if (!tooltipEl) {
      tooltipEl = document.createElement("div");
      tooltipEl.id = "custom-tooltip";
      tooltipEl.style.position = "absolute";
      tooltipEl.style.padding = "6px 8px";
      tooltipEl.style.borderRadius = "8px";
      tooltipEl.style.fontSize = "14px";
      tooltipEl.style.pointerEvents = "none";
      tooltipEl.style.opacity = "0";
      tooltipEl.style.transition = "opacity 0.2s ease";
      tooltipEl.style.fontFamily = 'proxima-nova';
      document.body.appendChild(tooltipEl);
    }

    // Set custom text and colors dynamically
    const datasetLabel = tooltipModel.dataPoints[0].dataset.label;
    const value = tooltipModel.dataPoints[0].raw.y;

    if (datasetLabel === "Average Score") {
      tooltipEl.style.background = 'white';
      tooltipEl.style.color = 'black';
      tooltipEl.style.border = `1px solid ${theme.colors.borderPalette.primary}`;
      tooltipEl.innerHTML = `<span>Bootcamp Avg: ${value}</span>`;
      tooltipEl.style.minWidth = 'initial';
    } else {
      tooltipEl.style.background = theme.colors.special.tuxedo;
      tooltipEl.style.borderColor = 'transparent';
      tooltipEl.style.color = 'white';
      tooltipEl.style.minWidth = '250px';

      const data = tooltipModel.dataPoints[0];
      const date = new Date(data.label).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
      const label = data.dataset.data[data.dataIndex].dataLabel.split(': ')[0].trim();

      tooltipEl.innerHTML = `
        <div style="display: flex; flex-direction: column; gap: 4px;">
        <span style="font-size: 16px; line-height: 150%;">${date}</span>
        <span style="font-size: 16px; line-height: 150%; font-weight: bold;">${label}</span>
        <div style="display: flex; flex-direction: column;">
          <div style="display: flex; flex-direction: row; justify-content: space-between; font-weight: bold; font-size: 16px; line-height: 150%; flex: 1; white-space: nowrap;">
            <span>Your Score</span>
            <span>${data.formattedValue}</span>
          </div>
          <div style="display: flex; flex-direction: row; justify-content: space-between; color: ${theme.colors.typographyPalette.tertiary}; font-size: 16px; line-height: 150%; flex: 1; white-space: nowrap;">
            <span>Bootcamp Avg.</span>
            <span>${subjectAverageScore}</span>
          </div>
        </div>
        </div>
      `;
    }

    // Get chart position for accurate tooltip placement
    const chartRect = context.chart.canvas.getBoundingClientRect();
    const tooltipWidth = tooltipEl.offsetWidth;
    const tooltipHeight = tooltipEl.offsetHeight;

    let tooltipX = chartRect.left + window.scrollX + tooltipModel.caretX;
    let tooltipY = chartRect.top + window.scrollY + tooltipModel.caretY - tooltipHeight - 10; // Default: above hovered point

    // Get viewport width and height
    const viewportWidth = window.innerWidth;
    const viewportHeight = window.innerHeight;


    if (datasetLabel === 'Average Score') {
      tooltipX = chartRect.right - 120; // Position to the right of the chart
      tooltipY = chartRect.top + window.scrollY + tooltipModel.caretY - tooltipHeight / 2; // Centered on the avg score line

      // Prevent tooltip from going off-screen
      if (tooltipX + tooltipWidth > viewportWidth - 10) {
        tooltipX = viewportWidth - tooltipWidth - 10;
      }
      if (tooltipY < window.scrollY + 10) {
        tooltipY = window.scrollY + 10;
      }
      if (tooltipY + tooltipHeight > viewportHeight - 10) {
        tooltipY = viewportHeight - tooltipHeight - 10;
      }
    } else {
      // **Prevent Tooltip from Going Off-Screen**
      if (tooltipX + tooltipWidth > viewportWidth - 10) {
        // If it overflows right, shift it left
        tooltipX -= (tooltipWidth + 20);
      }
      if (tooltipX < 10) {
        // If it overflows left, shift it right
        tooltipX = 10;
      }
      if (tooltipY < window.scrollY + 10) {
        // If it overflows top, move below the hovered point
        tooltipY = chartRect.top + window.scrollY + tooltipModel.caretY + 10;
      }
      if (tooltipY + tooltipHeight > viewportHeight - 10) {
        // If it overflows bottom, shift up slightly
        tooltipY = viewportHeight - tooltipHeight - 10;
      }
    }

    // Apply final positioning
    tooltipEl.style.left = `${tooltipX}px`;
    tooltipEl.style.top = `${tooltipY}px`;
    tooltipEl.style.opacity = "1"; // Make it visible
  }

  const chartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    type: 'line',
    normalized: true,
    scales: {
      x: {
        type: 'timeseries',
        time: {
          unit: 'day',
        },
        ticks: {
          font: {
            family: 'proxima-nova',
            size: 14,
            weight: 'bold',
          },
          color: '#C8C9CE',
          callback: function(value, index, values, ...rest) {
            const resultMatch = sortOrder === 'DESC'
              ? [...filteredTestResultsOverTime]?.reverse()?.[index]
              : filteredTestResultsOverTime?.[index];
            const formattedLabel = `Test #${resultMatch?.testNumber}`;

            return formattedLabel;
          }
        },
        title: {
          display: false,
          text: 'Date',
          font: {
            family: 'proxima-nova',
            size: 16,
            weight: 'bold',
          },
          color: '#C8C9CE'
        },
        grid: {
          display: false, // Hide the gridlines on the y-axis
        },
      },
      y: {
        min: 10,
        suggestedMax: maxChartValue,
        ticks: {
          font: {
            family: 'proxima-nova',
            size: 16,
            weight: 'bold',
          },
          color: '#85868E'
        },
        border: {
          display: false,
          dash: [2, 2],
        },
        grid: {
          tickBorderDash: [2, 2],
          tickBorderDashOffset: 0,
        },
        title: {
          display: true,
          text: `Estimated DAT Score`,
          font: {
            family: 'proxima-nova',
            size: 16,
            weight: 'bold',
          },
          color: '#85868E',
          padding: {
            bottom: 8,
          }
        }
      }
    },
    plugins: {
      tooltip: {
        enabled: false,
        external: customTooltipHandler,
      },
      legend: {
        display: false,
        position: 'bottom',
        labels: {
          boxWidth: 10,
          boxHeight: 10,
          usePointStyle: true,
          font: {
            family: 'proxima-nova',
          },
        }
      },
    },
  };


  return (
    <Container>
      <StyledHeader parentHasPadding={parentHasPadding}>
        <HeaderText>Performance Over Time</HeaderText>
      </StyledHeader>
      <AutoHeightParent childRef={wrapperRef}>
      <TableOverflowOuterWrapper parentHasPadding={parentHasPadding}>
        <TableOverflowInnerWrapper ref={wrapperRef} parentHasPadding={parentHasPadding}>
          <Wrapper>
            <SelectArea>
              {!subjectOverride && (
                <SubjectSelect value={selectedSubject} onChange={e => {
                  setSelectedSubject(e.target.value);
                }}>
                  <option disabled selected>Select a subject...</option>
                  {SUBJECTS.map(subject => <option value={subject}>{subject}</option>)}
                </SubjectSelect>
              )}
            </SelectArea>
                <ChartBackground>
                  {fetching ? <LoadingSpinner color="royal" active /> : filteredTestResultsOverTime?.length < 2 ? (
                    <div style={{
                      height: '100%',
                      width: '100%',
                      background: `url(${blurredBackground}) no-repeat center center`,
                      display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: 8}}>
                      <h3 style={{fontFamily: 'proxima-nova', fontSize: 18, fontWeight: 'bold', color: '#85868E'}}>Not enough data to display chart yet</h3>
                      <p style={{fontFamily: 'proxima-nova', fontSize: 16, fontWeight: 'normal', color: '#85868E'}}>Come back when you've completed at least 2 practice tests!</p>
                    </div>
                  ) : <Line ref={chartRef} data={chartData} options={chartOptions} />}
                </ChartBackground>
          </Wrapper>
        </TableOverflowInnerWrapper>
      </TableOverflowOuterWrapper>
      </AutoHeightParent>
    </Container>
  )
}